/**
 * Shared model for the redesigned mobile Orders screen (Trioty Orders Mobile):
 * the Active/Needs-action/All tabs, the status→group mapping used for grouping,
 * date bucketing, sort comparators, and the row-detail field list.
 *
 * All of this is derived from our real OrderStatus / SellerOrderListItem — the
 * design's simplified stages are mapped onto our richer status machine here so
 * grouping and tabs stay backed by real data.
 */

import type { OrderStatus, SellerOrderListItem, ShipmentStatus } from "@/lib/api/types";
import { keys } from "@/lib/i18n/portal";

export type OrdersTab = "active" | "all";
export type OrdersGroupBy = "status" | "date" | "channel" | "none";
export type OrdersSort = "newest" | "oldest" | "valueDesc" | "valueAsc";
export type RowProp =
  | "customer"
  | "items"
  | "value"
  | "time"
  | "location"
  | "payment"
  | "channel";

/** Coarse group buckets for the "Group by status" list, in display order. */
export type StatusGroup =
  | "awaiting_payment"
  | "to_confirm"
  | "preparing"
  | "in_transit"
  | "delivered"
  | "closed"
  | "draft";

/** Buyer payment kind, derived from paymentMethod + paid/required amounts. */
export type PaymentKind = "cod" | "paid" | "deposit";

/** Date-range preset for the filter sheet. */
export type DatePreset = "all" | "today" | "week" | "month";

/** Filter selections for the mobile Orders filter sheet. */
export type OrdersFilterState = {
  status: StatusGroup[];
  payment: PaymentKind[];
  governorate: string[];
  date: DatePreset;
  shipment: ShipmentStatus[];
};

// `draft` is intentionally last — drafts only appear on the "All" tab.
export const STATUS_GROUP_ORDER: StatusGroup[] = [
  "awaiting_payment",
  "to_confirm",
  "preparing",
  "in_transit",
  "delivered",
  "closed",
  "draft",
];

/** i18n label key for each status group header. */
export const STATUS_GROUP_LABEL: Record<StatusGroup, string> = {
  awaiting_payment: keys.orders.groupAwaitingPayment,
  to_confirm: keys.orders.groupToConfirm,
  preparing: keys.orders.groupPreparing,
  in_transit: keys.orders.groupInTransit,
  delivered: keys.orders.groupDelivered,
  closed: keys.orders.groupClosed,
  draft: keys.orders.groupDrafts,
};

/** Maps our OrderStatus onto a coarse group bucket. */
export function statusGroup(status: OrderStatus): StatusGroup {
  switch (status) {
    case "draft":
      return "draft";
    case "pending_payment_verification":
    case "paid_less_than_required":
    case "pending_buyer_payment":
    case "pending_buyer_verification":
      return "awaiting_payment";
    case "pending_seller_confirmation":
    case "confirmed":
      return "to_confirm";
    case "preparing":
      return "preparing";
    case "shipped":
      return "in_transit";
    case "delivered":
      return "delivered";
    case "cancelled":
    case "declined":
    default:
      return "closed";
  }
}

/** Tab membership: Active = in-flight (needs some action); All = everything.
 *  Drafts (incomplete orders) surface only on the "All" tab. */
export function inTab(status: OrderStatus, tab: OrdersTab): boolean {
  if (status === "draft") return tab === "all";
  if (tab === "all") return true;
  const g = statusGroup(status);
  return g !== "delivered" && g !== "closed";
}

// The attention taxonomy (seller-action vs buyer-waiting, urgency ranking, reason
// grouping) is shared with the desktop view — see ../lib/orders-attention-model.
export {
  ATTENTION_ORDER,
  isAttention,
  isWaitingOnBuyer,
  attentionRank,
} from "../../lib/orders-attention-model";

/** Resolves a date preset to inclusive ISO from/to bounds for the backend. */
export function dateBounds(preset: DatePreset): { dateFrom?: string; dateTo?: string } {
  if (preset === "all") return {};
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (preset !== "today") {
    start.setDate(start.getDate() - (preset === "week" ? 6 : 29));
  }
  return { dateFrom: start.toISOString(), dateTo: end.toISOString() };
}

export const DATE_PRESET_LABEL: Record<DatePreset, string> = {
  all: keys.orders.dateAllTime,
  today: keys.orders.dateToday,
  week: keys.orders.dateThisWeek,
  month: keys.orders.dateThisMonth,
};

export const SHIPMENT_STATUS_LABEL: Record<ShipmentStatus, string> = {
  pending_pickup: keys.orders.shipmentStatusPendingPickup,
  picked_up: keys.orders.shipmentStatusPickedUp,
  in_transit: keys.orders.shipmentStatusInTransit,
  delivered: keys.orders.shipmentStatusDelivered,
  returned: keys.orders.shipmentStatusReturned,
  exception: keys.orders.shipmentStatusException,
  cancelled: keys.orders.shipmentStatusCancelled,
  lost: keys.orders.shipmentStatusLost,
  on_hold: keys.orders.shipmentStatusOnHold,
};

export const SHIPMENT_STATUS_ORDER: ShipmentStatus[] = [
  "pending_pickup",
  "picked_up",
  "in_transit",
  "delivered",
  "returned",
  "exception",
  "on_hold",
  "cancelled",
  "lost",
];

export type DateBucket = "today" | "yesterday" | "earlier";

/** Buckets an ISO date into Today / Yesterday / Earlier relative to `now`. */
export function dateBucket(createdAtIso: string, now: Date): DateBucket {
  const d = new Date(createdAtIso);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  if (d >= startOfToday) return "today";
  if (d >= startOfYesterday) return "yesterday";
  return "earlier";
}

export const DATE_BUCKET_LABEL: Record<DateBucket, string> = {
  today: keys.orders.dateToday,
  yesterday: keys.orders.groupYesterday,
  earlier: keys.orders.groupEarlier,
};

/** Client-side comparators. Backend already honours newest/oldest for the page;
 *  value sorts run over the loaded set. */
export function sortComparator(
  sort: OrdersSort,
): (a: SellerOrderListItem, b: SellerOrderListItem) => number {
  switch (sort) {
    case "oldest":
      return (a, b) => a.createdAt.localeCompare(b.createdAt);
    case "valueDesc":
      return (a, b) => b.totalAmount - a.totalAmount;
    case "valueAsc":
      return (a, b) => a.totalAmount - b.totalAmount;
    case "newest":
    default:
      return (a, b) => b.createdAt.localeCompare(a.createdAt);
  }
}

/** The status set a given tab maps to on the backend, so the query only fetches
 *  what the tab shows (All → undefined = no status filter). */
export function tabStatuses(tab: OrdersTab): OrderStatus[] | undefined {
  const ALL: OrderStatus[] = [
    "paid_less_than_required",
    "pending_buyer_verification",
    "pending_buyer_payment",
    "pending_payment_verification",
    "pending_seller_confirmation",
    "confirmed",
    "preparing",
    "shipped",
    "delivered",
    "cancelled",
    "declined",
  ];
  if (tab === "all") return undefined;
  return ALL.filter((s) => inTab(s, tab));
}
